import React from "react";
import _ from "lodash";
import {useSelector} from 'react-redux';
import {PageHeader} from "components/molecules/PageHeader/PageHeader";
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import BoardTable from "components/atoms/BoardTable/BoardTable";
import Typography from "@mui/material/Typography";
import {Alert, Grid, Skeleton} from "@mui/material";
import Button from "@mui/material/Button";
import {Box} from "@mui/system";
import {Link as RouterLink} from "react-router-dom";
import {routes} from "routes";
import {selectAllSubscriptionsLive} from "redux/subscription/subscriptionSlice";
import MenuItem from "@mui/material/MenuItem";
import SettingsDropdown from "components/molecules/SettingsDropdown/SettingsDropdown";
import {
  formatPercentage,
  formatCurrency,
  getValueColor
} from "utils/metricFormatters";

// Helper function to safely convert values to numbers
const safeParseFloat = (value) => {
  if (value === null || value === undefined) return 0;
  // Handle both string and number inputs
  const numberValue = typeof value === 'string' ? parseFloat(value) : Number(value);
  return isNaN(numberValue) ? 0 : numberValue;
};

// Helper function to format ISO date to YYYY-MM-DD
const formatDate = (isoDate) => {
  if (!isoDate) return 'N/A';
  return new Date(isoDate).toISOString().split('T')[0];
};


export default function Live() {

  const subscriptions = useSelector(selectAllSubscriptionsLive);
  const strategies = useSelector(state => state.strategy.entities);
  const loading = useSelector(state => state.subscription.loading);

  return (
    <>
      <PageHeader text1="Live" text2="Strategies" title="Dashboard - Live" description="These are strategies for which you have auto-trading enabled."  />

      <Box sx={{mt: 5}}>

        {_.isEmpty(subscriptions) ?
          <Box sx={{mt: 4}}>
            <Alert severity="info">You have no Live auto-trading strategies.</Alert>

          </Box> :
         <BoardTable fullWidth>
          <TableHead>

            <TableRow>
              <TableCell rowSpan={2}>Strategy</TableCell>

              <TableCell align="center">Started</TableCell>
              <TableCell align="center">Today P/L</TableCell>
              <TableCell align="center">Since Live</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subscriptions.map((subscription, index) => {
              const strategy = strategies[subscription.strategy];
              
              // Get metrics from subscription performance_metrics
              const metrics = subscription.performance_metrics || {};
              const todayChange = metrics.value_change_today || { value: 0, percentage: 0 };
              const totalChange = metrics.value_change_total || { value: 0, percentage: 0 };
              
              // Format start date - use autotrading_started date if available, otherwise creation date
              const startDate = formatDate(subscription.autotrading_started || subscription.created);
              
              // Format change values with currency
              const todayChangeValue = formatCurrency(safeParseFloat(todayChange.value), 0);
              const totalChangeValue = formatCurrency(safeParseFloat(totalChange.value), 0);
              
              return (
                  <TableRow key={subscription.id}>
                    <TableCell className="light2" >
                      <Grid container>
                        <Grid item md={12} lg={6}>
                          <RouterLink to={routes.strategyDetails.replace(":strategyId", strategy.id)}>
                            <Typography variant="h4" sx={{mb:1}} color="primary"> {strategy.name.toUpperCase()} </Typography>
                          </RouterLink>
                        </Grid>
                        <Grid item md={12} lg={6}>
                          <Box display="flex" justifyContent={{xs: "flex-start", lg: "flex-end"}} mt={{xs: 1, lg: 0}}>
                          <RouterLink to={routes.strategyDetails.replace(":strategyId", strategy.id)}>
                            <Button color="primary" variant="outlined" size="small" > Details </Button>
                          </RouterLink>
                          </Box>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell align="center" className="light">
                      {loading ? (
                        <Skeleton width={80} height={24} />
                      ) : (
                        <Typography variant="largeText" color="secondary.dark">{startDate}</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center" className="light">
                      {loading ? (
                        <Skeleton width={80} height={24} />
                      ) : (
                        <Typography 
                          variant="largeText" 
                          color={getValueColor(safeParseFloat(todayChange.value))}
                        >
                          {todayChangeValue}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center" className="light">
                      {loading ? (
                        <Skeleton width={80} height={24} />
                      ) : (
                        <Typography 
                          variant="largeText" 
                          color={getValueColor(safeParseFloat(totalChange.value))}
                        >
                          {totalChangeValue}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{width: 80}}>
                      <SettingsDropdown>
                        <MenuItem component={RouterLink} to={`${routes.autoTrade}`} >Manage Trading &nbsp;(AutoTrade)</MenuItem>
                      </SettingsDropdown>
                    </TableCell>
                  </TableRow>
              )
            })}

          </TableBody>
        </BoardTable>
        }
      </Box>

    </>
  )
}